// Analytics tracking utility for cart abandonment and page visits
const API = process.env.REACT_APP_BACKEND_URL;

// Check if current user is an admin (skip all tracking for admins)
const isAdmin = () => !!localStorage.getItem('token');

// Get or create a persistent session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Track checkout page visit with cart data
export const trackCheckoutVisit = async (cartItems, cartTotal, deliveryMethod = null) => {
  if (isAdmin()) return;
  try {
    const sessionId = getSessionId();
    
    await fetch(`${API}/api/analytics/checkout-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        cart_items: cartItems.map(item => ({
          shrimp_id: item.shrimp_id,
          shrimp_name: item.shrimp_name,
          quantity: item.quantity,
          unit_price: item.unit_price || 0,
          total_price: item.total_price || 0
        })),
        cart_total: cartTotal,
        delivery_method: deliveryMethod
      })
    });
  } catch (error) {
    // Silent fail - don't break user experience for analytics
    console.debug('Analytics: checkout visit tracking failed', error);
  }
};

// Mark checkout as converted (called when order is placed)
export const trackCheckoutConverted = async (orderId) => {
  if (isAdmin()) return;
  try {
    const sessionId = getSessionId();
    
    await fetch(`${API}/api/analytics/checkout-converted?session_id=${sessionId}&order_id=${orderId}`, {
      method: 'POST'
    });
  } catch (error) {
    console.debug('Analytics: conversion tracking failed', error);
  }
};

// Track general page visit
export const trackPageVisit = async (page) => {
  if (isAdmin()) return;
  try {
    const sessionId = getSessionId();
    
    await fetch(`${API}/api/analytics/page-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: page,
        session_id: sessionId
      })
    });
  } catch (error) {
    console.debug('Analytics: page visit tracking failed', error);
  }
};

export { getSessionId };

// ---------------------------------------------------------------------------
// Google Analytics 4 (GA4) ecommerce events
// ---------------------------------------------------------------------------
// Fire the standard GA4 ecommerce events so Google reports add-to-cart, checkout
// and (most importantly) purchase + revenue. These are safe no-ops whenever gtag
// isn't loaded — which is the case for logged-in admins AND on any non-production
// hostname (see the ga-disable guard in public/index.html). So they never send
// events for admins or from the preview/dev environment.
const GA_CURRENCY = 'EUR';

export const trackGAEvent = (name, params = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  } catch (error) {
    console.debug('GA event failed', error);
  }
};

const toGAItem = (line) => ({
  item_id: String(line.shrimp_id || line.id || line.cart_item_id || 'unknown'),
  item_name: line.shrimp_name || line.name || 'Product',
  price: Number(line.unit_price ?? line.price ?? 0),
  quantity: Number(line.quantity ?? 1),
  ...(line.selected_grade ? { item_variant: line.selected_grade } : {}),
});

export const gaViewItem = (product) => {
  try {
    if (!product) return;
    const price = Number(product.price ?? 0);
    trackGAEvent('view_item', {
      currency: GA_CURRENCY,
      value: price,
      items: [{
        item_id: String(product.id || product.slug || 'unknown'),
        item_name: product.name || 'Product',
        price,
      }],
    });
  } catch (error) {
    console.debug('gaViewItem failed', error);
  }
};

export const gaAddToCart = (line) => {
  try {
    const item = toGAItem(line);
    trackGAEvent('add_to_cart', {
      currency: GA_CURRENCY,
      value: Number((item.price * item.quantity).toFixed(2)),
      items: [item],
    });
  } catch (error) {
    console.debug('gaAddToCart failed', error);
  }
};

export const gaBeginCheckout = (cartItems, cartTotal) => {
  try {
    trackGAEvent('begin_checkout', {
      currency: GA_CURRENCY,
      value: Number(Number(cartTotal || 0).toFixed(2)),
      items: (cartItems || []).map(toGAItem),
    });
  } catch (error) {
    console.debug('gaBeginCheckout failed', error);
  }
};

export const gaPurchase = (order) => {
  try {
    if (!order) return;
    const txId = order.order_number || order.id;
    if (!txId) return;
    // Dedupe: never count the same order twice (refresh-safe).
    try {
      const fired = JSON.parse(localStorage.getItem('ga_purchases') || '[]');
      if (fired.includes(txId)) return;
      fired.push(txId);
      localStorage.setItem('ga_purchases', JSON.stringify(fired.slice(-50)));
    } catch (error) {
      console.debug('ga_purchases dedupe storage unavailable', error);
    }
    trackGAEvent('purchase', {
      transaction_id: String(txId),
      currency: GA_CURRENCY,
      value: Number(Number(order.total_amount ?? order.total ?? 0).toFixed(2)),
      shipping: Number((Number(order.shipping_fee || 0) + Number(order.heatpack_fee || 0)).toFixed(2)),
      items: (order.items || []).map(toGAItem),
    });
  } catch (error) {
    console.debug('gaPurchase failed', error);
  }
};
